import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaTrashRestore,
  FaUndo,
  FaTrash,
  FaSync,
  FaClock,
} from "react-icons/fa";
import { useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api/axiosInstance";
import getImageUrl from "../../../utils/imageUtils";
import { useToast } from "../../../contexts/ToastContext";
import ConfirmModal from "../../../components/common/ConfirmModal";
import useBulkSelection from "../../../components/profile/bulk/useBulkSelection";
import BulkSelectBar, {
  SelectCheckbox,
  SelectModeButton,
} from "../../../components/profile/bulk/BulkSelectBar";
import bulkStyles from "../../../components/profile/bulk/BulkSelectBar.module.css";
import runBulkRequests from "../../../utils/runBulkRequests";
import Loader from "../../../components/common/Loader";
import sharedStyles from "../../../styles/pages/profile/SharedProfile.module.css";
import styles from "../../../styles/pages/profile/RecentlyDeletedPosts.module.css";

const formatDeletedDate = (dateStr) => {
  if (!dateStr) return "Unknown";
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getPostPreview = (post) => {
  if (post.caption?.trim()) {
    return post.caption.trim().slice(0, 120) + (post.caption.length > 120 ? "…" : "");
  }
  if (post.products?.length) {
    return post.products.map((p) => p.name).filter(Boolean).join(", ");
  }
  if (post.category?.name) return post.category.name;
  return "Post";
};

const RecentlyDeletedPosts = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [recoveryDays, setRecoveryDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [pending, setPending] = useState(null);
  const visibleIds = useMemo(
    () => posts.map((post) => post._id).filter(Boolean),
    [posts],
  );
  const {
    selectMode,
    selectedIds,
    setSelectedIds,
    allVisibleSelected,
    toggle,
    selectAllVisible,
    exitSelectMode,
    toggleSelectMode,
  } = useBulkSelection(visibleIds);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await api.get("/posts/me/recently-deleted");
      if (response.data.success) {
        setPosts(response.data.posts || []);
        setTotal(response.data.total || 0);
        setRecoveryDays(response.data.recoveryDays || 30);
      }
    } catch (error) {
      console.error("Error fetching recently deleted posts:", error);
      showToast({ message: "Failed to load recently deleted posts", type: "error" });
      setLoadError("We couldn't load your recently deleted posts.");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const openPending = (type, ids) => {
    if (!ids.length || actionId) return;
    setPending({ type, ids });
  };

  const handlePending = async () => {
    if (!pending) return;
    const { type, ids } = pending;
    const restoring = type === "restore";
    try {
      setActionId("bulk");
      setPending(null);
      const { succeeded, failed, failedIds } = await runBulkRequests(ids, (id) =>
        restoring
          ? api.post(`/posts/me/recently-deleted/${id}/restore`)
          : api.delete(`/posts/me/recently-deleted/${id}`),
      );
      if (succeeded > 0) {
        queryClient.invalidateQueries({ queryKey: ["feed"] });
        showToast({
          message: restoring
            ? succeeded === 1
              ? "Post restored"
              : `${succeeded} posts restored`
            : succeeded === 1
              ? "Post deleted forever"
              : `${succeeded} posts deleted forever`,
          type: "success",
        });
        await fetchPosts();
      }
      if (failed > 0) {
        showToast({
          message: restoring
            ? "Some posts could not be restored"
            : "Some posts could not be deleted",
          type: "error",
        });
        setSelectedIds(failedIds);
      } else if (selectMode) {
        exitSelectMode();
      }
    } catch (error) {
      console.error("Error updating recently deleted posts:", error);
      showToast({
        message: error.response?.data?.message || "Failed to update posts",
        type: "error",
      });
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className={sharedStyles.contentSection}>
      <div className={sharedStyles.sectionHeader}>
        <div>
          <h2 className={sharedStyles.sectionTitle}>
            <FaTrashRestore className={sharedStyles.sectionIcon} aria-hidden />
            Recently Deleted
          </h2>
          <p className={sharedStyles.sectionSubtitle}>
            Deleted posts are kept for {recoveryDays} days. Restore them or delete them permanently.
          </p>
        </div>
        <div className={sharedStyles.bulkActions}>
          {posts.length > 0 && (
            <SelectModeButton
              active={selectMode}
              onClick={toggleSelectMode}
              disabled={Boolean(actionId)}
            />
          )}
          <button
            type="button"
            className={sharedStyles.actionButton}
            onClick={fetchPosts}
            disabled={loading}
          >
            <FaSync aria-hidden /> Refresh
          </button>
        </div>
      </div>

      {selectMode && (
        <BulkSelectBar
          selectedCount={selectedIds.length}
          allSelected={allVisibleSelected}
          busy={Boolean(actionId)}
          busyLabel="Working..."
          onSelectAll={selectAllVisible}
          onDelete={() => openPending("delete", selectedIds)}
          deleteLabel="Delete forever"
          noun="posts"
          extra={
            <button
              type="button"
              className={`buttonSecondary ${bulkStyles.bulkRestore}`}
              onClick={() => openPending("restore", selectedIds)}
              disabled={selectedIds.length === 0 || Boolean(actionId)}
            >
              <FaUndo aria-hidden />
              {selectedIds.length > 0
                ? `Restore (${selectedIds.length})`
                : "Restore"}
            </button>
          }
        />
      )}

      {loading ? (
        <div className={sharedStyles.loading}>
          <Loader />
        </div>
      ) : loadError ? (
        <div className={sharedStyles.emptyState}>
          <FaTrashRestore className={sharedStyles.emptyStateIcon} size={48} aria-hidden />
          <h3>Couldn't load this list</h3>
          <p className={sharedStyles.emptyStateSubtext}>{loadError}</p>
          <button type="button" className={sharedStyles.actionButton} onClick={fetchPosts}>
            Retry
          </button>
        </div>
      ) : posts.length === 0 ? (
        <div className={sharedStyles.emptyState}>
          <FaTrashRestore className={sharedStyles.emptyStateIcon} size={48} aria-hidden />
          <h3>No recently deleted posts</h3>
          <p className={sharedStyles.emptyStateSubtext}>
            When you delete a post, it will appear here for {recoveryDays} days before being removed automatically.
          </p>
        </div>
      ) : (
        <>
          <p className={styles.summary}>
            {total} deleted post{total === 1 ? "" : "s"} · recoverable for up to {recoveryDays} days
          </p>
          <ul className={styles.list}>
            {posts.map((post) => {
              const thumb = post.images?.[0];
              const isBusy = Boolean(actionId);
              const isSelected = selectedIds.includes(post._id);

              return (
                <li
                  key={post._id}
                  className={`${styles.card} ${
                    selectMode ? bulkStyles.selectable : ""
                  } ${isSelected ? bulkStyles.cardSelected : ""}`}
                  onClick={(event) => {
                    if (!selectMode) return;
                    if (event.target.closest("button, input, label")) return;
                    toggle(post._id);
                  }}
                >
                  <div className={styles.thumb}>
                    {thumb ? (
                      <img src={getImageUrl(thumb)} alt="" />
                    ) : (
                      <div className={styles.thumbPlaceholder}>
                        <FaTrashRestore aria-hidden />
                      </div>
                    )}
                  </div>

                  <div className={styles.details}>
                    <p className={styles.preview}>{getPostPreview(post)}</p>
                    <div className={styles.meta}>
                      <span>Deleted {formatDeletedDate(post.deletedAt)}</span>
                      <span className={styles.metaDivider}>·</span>
                      <span className={styles.daysLeft}>
                        <FaClock aria-hidden />
                        {post.daysRemaining} day{post.daysRemaining === 1 ? "" : "s"} left
                      </span>
                    </div>
                  </div>

                  <div className={styles.actions}>
                    {selectMode ? (
                      <SelectCheckbox
                        checked={isSelected}
                        onChange={() => toggle(post._id)}
                        label={`Select ${getPostPreview(post)}`}
                        disabled={isBusy}
                      />
                    ) : (
                      <>
                        <button
                          type="button"
                          className={styles.restoreBtn}
                          disabled={isBusy}
                          onClick={() => openPending("restore", [post._id])}
                        >
                          <FaUndo aria-hidden /> Restore
                        </button>
                        <button
                          type="button"
                          className={styles.deleteBtn}
                          disabled={isBusy}
                          onClick={() => openPending("delete", [post._id])}
                        >
                          <FaTrash aria-hidden /> Delete forever
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {pending && (
        <ConfirmModal
          message={
            pending.type === "restore"
              ? pending.ids.length > 1
                ? `Restore ${pending.ids.length} posts? They will reappear on your profile.`
                : "Restore this post? It will reappear on your profile."
              : pending.ids.length > 1
                ? `Permanently delete ${pending.ids.length} posts? This cannot be undone.`
                : "Permanently delete this post? This cannot be undone."
          }
          onConfirm={handlePending}
          onClose={() => {
            if (actionId) return;
            setPending(null);
          }}
        />
      )}
    </div>
  );
};

export default RecentlyDeletedPosts;
